import React, { useCallback, useMemo } from 'react';
import { List } from 'immutable';
import { useSelector } from 'react-redux';
import { chatAllUsersByIdSelector } from '@/domain/chat';
import isEmpty from 'lodash/isEmpty';

import { SYMBOL_FOR_EMPTY_CELL } from '@/components/AgGrid/helpers';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import CountChip from '@/components/AgGrid/components/TagsCellRenderer/CountChip';
import useObserverTags from '@/components/AgGrid/components/TagsCellRenderer/useObserverTags';

const TagsCellRenderer = ({ valueFormatted, value }) => {
  const usersById = useSelector(chatAllUsersByIdSelector);

  const isUserTag = useCallback((tag) => Boolean(usersById.get(tag)), [usersById]);

  const tagList = useMemo(
    () => (value ? value.sort((tagA, tagB) => isUserTag(tagB) - isUserTag(tagA)) : []),
    [isUserTag, value],
  );

  const getChipProps = useCallback(
    (tag) => {
      const user = usersById.get(tag);
      return {
        label: user ? user.username : tag,
        avatar: user ? <Avatar src={user.picture} alt={user.username} /> : undefined,
      };
    },
    [usersById],
  );

  const { isAllHidden, lastVisibleKey, hiddenCount, setElementRef, parentElRef, elementsVisibilityState } =
    useObserverTags({ uniqueKeysList: List(tagList) });

  if (tagList.length === 0) {
    return <Box sx={{ px: 2 }}>{isEmpty(valueFormatted) ? SYMBOL_FOR_EMPTY_CELL : valueFormatted}</Box>;
  }

  return (
    <Stack direction="row" flexWrap="nowrap" spacing={0.5} maxWidth="100%" px={0.5} ref={parentElRef}>
      {isAllHidden && <CountChip size="small" label={tagList.length} />}

      {tagList.map((tag) => {
        const isVisible = elementsVisibilityState.get(tag, 1) === 1;
        const isLastVisible = lastVisibleKey === tag;
        const shouldShowCountChip = isLastVisible && hiddenCount > 0 && !isAllHidden;

        return (
          <Stack key={tag} direction="row" flexWrap="nowrap" spacing={0.5} ref={setElementRef(tag)}>
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              {...getChipProps(tag)}
              sx={{
                backgroundColor: 'common.white',
                opacity: isVisible ? 1 : 0,
                // Keep the element in layout even when hidden to prevent layout shifts
                visibility: isVisible ? 'visible' : 'hidden',
              }}
            />
            {/* Show count chip after the last visible tag */}
            {shouldShowCountChip && <CountChip size="small" label={hiddenCount} />}
          </Stack>
        );
      })}
    </Stack>
  );
};

export default React.memo(TagsCellRenderer);
